import React, { useContext, useState } from "react";
import { UserContext } from "./Context/UserContext";
import { Link, NavLink } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  Bell,
  ShoppingBag,
  User,
} from "lucide-react";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const { user } = useContext(UserContext);

  const navLinkClass = ({ isActive }) =>
    `relative py-2 text-sm font-semibold transition-colors duration-300 ${
      isActive
        ? "text-blue-600"
        : "text-slate-700 hover:text-blue-600"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/90 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="h-[72px] flex items-center justify-between">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            onClick={() => setIsOpen(false)}
          >
            <div className="relative">
              <img
                src="/logo.png"
                alt="Nexindi Tech"
                className="h-11 w-11 rounded-xl object-cover border border-slate-200 shadow-sm"
              />

              <span className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-600 border-2 border-white rounded-full"></span>
            </div>

            <div className="hidden sm:block leading-tight">
              <h1 className="text-lg font-bold tracking-tight text-slate-900">
                NEXINDI
              </h1>

              <span className="text-[10px] uppercase tracking-[0.22em] text-blue-600 font-semibold">
                Tech
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-7">
            <NavLink to="/" className={navLinkClass}>
              Home
            </NavLink>

            <NavLink to="/about" className={navLinkClass}>
              About
            </NavLink>

            <NavLink to="/services" className={navLinkClass}>
              Services
            </NavLink>

            <NavLink to="/our-projects" className={navLinkClass}>
              Projects
            </NavLink>

            <NavLink to="/blog" className={navLinkClass}>
              Insights
            </NavLink>

            {/* More Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setMoreOpen(true)}
              onMouseLeave={() => setMoreOpen(false)}
            >
              <button className="flex items-center gap-1 py-2 text-sm font-semibold text-slate-700 hover:text-blue-600 transition-colors">
                More
                <ChevronDown
                  size={15}
                  className={`transition-transform duration-300 ${
                    moreOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {moreOpen && (
                <div className="absolute top-full right-0 pt-3 w-56">
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-900/10 p-2">
                    <Link
                      to="/shop"
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    >
                      <ShoppingBag size={17} />
                      Shop
                    </Link>

                    <Link
                      to="/post-blog"
                      className="block px-4 py-3 rounded-xl text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
                    >
                      Post Blog
                    </Link>

                    {user && (
                      <>
                        <Link
                          to="/orders"
                          className="block px-4 py-3 rounded-xl text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
                        >
                          My Orders
                        </Link>

                        <Link
                          to="/notifications"
                          className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
                        >
                          <Bell size={17} />
                          Notifications
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Desktop Right Side */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                {/* Notification */}
                <Link
                  to="/notifications"
                  className="relative w-10 h-10 rounded-xl flex items-center justify-center text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition"
                >
                  <Bell size={20} />

                  {/* Optional notification dot */}
                  <span className="absolute top-2 right-2 w-2 h-2 bg-blue-600 rounded-full border-2 border-white"></span>
                </Link>

                {/* Profile */}
                <Link
                  to="/profile"
                  className="flex items-center gap-3 pl-2 pr-3 py-1.5 rounded-xl border border-slate-200 hover:border-blue-200 hover:bg-blue-50 transition"
                >
                  <img
                    src={user.profile_image || "/default-avatar.png"}
                    className="w-9 h-9 rounded-lg object-cover"
                    alt="Profile"
                  />

                  <div className="max-w-[100px]">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {user.name || "Account"}
                    </p>

                    <p className="text-[11px] text-slate-500">
                      View profile
                    </p>
                  </div>
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/signin"
                  className="px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-blue-600 transition"
                >
                  Sign in
                </Link>

                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-600/20"
                >
                  Start a Project

                  <span className="transition-transform group-hover:translate-x-1">
                    →
                  </span>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden w-11 h-11 rounded-xl border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
            aria-label="Toggle navigation"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          isOpen
            ? "max-h-[850px] opacity-100 border-t border-slate-200"
            : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-white px-5 py-5">
          <nav className="flex flex-col">

            {[
              { name: "Home", path: "/" },
              { name: "About Us", path: "/about" },
              { name: "Services", path: "/services" },
              { name: "Our Projects", path: "/our-projects" },
              { name: "Blogs", path: "/blog" },
              { name: "Shop", path: "/shop" },
            ].map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3.5 rounded-xl font-semibold text-sm transition ${
                    isActive
                      ? "bg-blue-50 text-blue-600"
                      : "text-slate-700 hover:bg-slate-50 hover:text-blue-600"
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}

            {user && (
              <>
                <Link
                  to="/orders"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-3.5 rounded-xl font-semibold text-sm text-slate-700 hover:bg-slate-50 hover:text-blue-600"
                >
                  My Orders
                </Link>

                <Link
                  to="/notifications"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-3.5 rounded-xl font-semibold text-sm text-slate-700 hover:bg-slate-50 hover:text-blue-600"
                >
                  Notifications
                </Link>
              </>
            )}
          </nav>

          <div className="my-4 border-t border-slate-200"></div>

          {/* Mobile Account Area */}
          {user ? (
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-4 p-3 rounded-2xl bg-slate-50 border border-slate-200"
            >
              <img
                src={user.profile_image || "/default-avatar.png"}
                className="w-12 h-12 rounded-xl object-cover"
                alt="Profile"
              />

              <div>
                <p className="font-bold text-slate-900">
                  {user.name || "My Account"}
                </p>

                <p className="text-sm text-slate-500">
                  View your profile
                </p>
              </div>
            </Link>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/signin"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center py-3.5 rounded-xl border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50"
              >
                Sign in
              </Link>

              <Link
                to="/signup"
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center py-3.5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition"
              >
                Sign up
              </Link>
            </div>
          )}

          {/* Mobile CTA */}
          <Link
            to="/contact"
            onClick={() => setIsOpen(false)}
            className="mt-4 flex items-center justify-center gap-2 py-4 rounded-xl bg-slate-950 text-white font-semibold text-sm hover:bg-blue-600 transition-colors"
          >
            Start a Project
            <span>→</span>
          </Link>
        </div>
      </div>
    </header>
  );
}